import { ClipboardText, ListPlus } from "@/components/shared/icons";
import { ListHeading } from "@/components/shared/list-header";
import { Typography } from "@/components/shared/typography";
import { Badge, RadioGroup, View } from "@/components/ui";
import { LashesAddOns, getLashesPackageFromCode } from "@/constants/booking";
import { useBookingState } from "@/store/booking/useBookingState";
import { useEffect, useMemo } from "react";
import { Divider } from "@/components/shared/divider";
import { formatPrice } from "@/lib/helpers/number";
import { ServiceRadioCard } from "@/components/shared/booking/service-radio-card";
import { PackageServiceTypeEnum } from "@/__generated__/graphql";
import { ServiceCreditTopUpCard } from "../components/service-credit-topup-card";

export const LashBooking = () => {
  const {
    data: { packageDetails, packageDetailsFilter, selectedPackageIds },
    setBookingState,
  } = useBookingState();

  const unit = useMemo(() => {
    return packageDetailsFilter?.unitTypes?.[0];
  }, [packageDetailsFilter?.unitTypes?.[0]]);

  const currentPackage = useMemo(() => {
    return (
      packageDetails
        .filter((pkg) => pkg.unitType === unit)
        .map((pkg) => ({ ...pkg, ...getLashesPackageFromCode(pkg.code) }))
        .sort((a, b) => a.order - b.order) || []
    );
  }, [packageDetails, unit]);

  const addOnPackages = useMemo(() => {
    return (
      packageDetails
        ?.filter(
          (pkg) =>
            pkg.serviceType === PackageServiceTypeEnum.AddOn &&
            LashesAddOns.includes(pkg.code),
        )
        .map((pkg) => ({ ...pkg, ...getLashesPackageFromCode(pkg.code) }))
        .sort((a, b) => a.order - b.order) || []
    );
  }, [packageDetails]);

  const onSelectPackage = (id: string) => {
    const addOnPackageIds = addOnPackages.map((pkg) => pkg.id);
    const isAddOnId = addOnPackageIds.includes(id);
    if (isAddOnId) {
      if (selectedPackageIds.includes(id)) {
        setBookingState({
          selectedPackageIds: selectedPackageIds.filter((sid) => sid !== id),
        });
      } else {
        setBookingState({
          selectedPackageIds: [...new Set([...selectedPackageIds, id])],
        });
      }
    } else {
      setBookingState({
        selectedPackageIds: selectedPackageIds
          .filter((sid) => addOnPackageIds.includes(sid))
          .concat([id]),
      });
    }
  };

  useEffect(() => {
    if (!unit || !currentPackage.length) return;
    const addOnPackageIds = addOnPackages.map((pkg) => pkg.id);
    const hasMainPackage = selectedPackageIds.some(
      (id) => !addOnPackageIds.includes(id),
    );
    const firstValidPackage = currentPackage.find((pkg) => pkg.title) || null; // can't direcly get the package from [0], somehow. e.g.: ["", "", "Classic 1D", "2D YY Lash", "3-4D Lash", "5-6D Lash"]
    if (!hasMainPackage && firstValidPackage) {
      setBookingState({ selectedPackageIds: [firstValidPackage.id] });
    }
  }, [unit, currentPackage, addOnPackages]);

  return (
    <View className="mt-2 flex flex-col gap-4">
      <View className="mx-4">
        <ServiceCreditTopUpCard service="beauty" />
      </View>

      <Divider className="border-b-2" />

      <View className="mx-4 flex flex-col gap-2">
        <ListHeading title="Choose Your Lash Type" icon={ClipboardText} />
        <Typography color="foreground-intermediate" variant="body-md">
          Pick the specific style based on your selected service.
        </Typography>
        <RadioGroup
          value={
            selectedPackageIds.filter(
              (id) => addOnPackages.findIndex((pkg) => pkg.id === id) === -1,
            )[0]
          }
          onValueChange={() => {}}
        >
          {currentPackage.map((pkg) => {
            if (!pkg.title) return null;
            return (
              <ServiceRadioCard
                subTitle={formatPrice(pkg.unitValue)}
                thirdTitle={`${pkg.duration * 60} mins`}
                key={pkg.title}
                active={selectedPackageIds.includes(pkg.id)}
                imgUrl={pkg.imgUrl}
                value={pkg.id}
                title={pkg.title}
                onSelect={() => onSelectPackage(pkg.id)}
              />
            );
          })}
        </RadioGroup>
      </View>

      <Divider className="my-2 border-b-2" />

      <View className="mx-4 flex flex-col gap-2">
        <View className="flex flex-row items-center gap-2">
          <ListHeading title="Add-Ons" icon={ListPlus} />
          <Badge variant="neutral" size="sm" className="border-none">
            Optional
          </Badge>
        </View>
        <Typography color="foreground-intermediate" variant="body-md">
          Customize your lash experience with these extras
        </Typography>
        <RadioGroup value="true" onValueChange={() => {}}>
          {addOnPackages?.map((pkg) => {
            if (!pkg.title) return;
            return (
              <ServiceRadioCard
                key={pkg.code}
                title={pkg.title}
                imgUrl={pkg.imgUrl}
                subTitle={formatPrice(pkg.unitValue)}
                thirdTitle={`${pkg.duration * 60} mins`}
                value={String(selectedPackageIds.includes(pkg.id))}
                onSelect={() => onSelectPackage(pkg.id)}
                active={selectedPackageIds.includes(pkg.id)}
              />
            );
          })}
        </RadioGroup>
      </View>

      <Divider className="my-2 border-b-2" />
    </View>
  );
};
